import { Trans } from "@lingui/react/macro";
import { Box, Container, Link as MuiLink, Stack, Typography } from "@mui/material";
import AppLayout from "@/components/AppLayout";
import BackToBookingButton from "@/components/BackToBookingButton";

export default function PrivacyPolicy() {
  return (
    <AppLayout contentMaxWidth="md">
      <Container maxWidth="md" sx={{ py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <BackToBookingButton />
        <Typography variant="h2" gutterBottom sx={{ mb: 3 }}>
          <Trans>Privacy Policy</Trans>
        </Typography>
        <Stack spacing={1.5}>
          <Typography variant="body1">
            <strong>
              <Trans>I. General information and legal basis</Trans>
            </strong>
          </Typography>
          <Typography variant="body1">
            <Trans>
              The following statement informs you about what kind of personal data is collected for
              what purpose on this website by us as the responsible party and to what extent this
              data is made available to third parties.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <strong>
              <Trans>1. Controller:</Trans>
            </strong>
          </Typography>
          <Typography variant="body1">
            <Trans>
              CampNerd GmbH & Co KG
              <br />
              Spandauer Burgwall 22a
              <br />
              13581 Berlin
              <br />
              Telephone: 030 - 343 885 80
              <br />
              Email: <MuiLink href="mailto:office@campnerd.camp">office@campnerd.camp</MuiLink>
            </Trans>
          </Typography>
          <Typography variant="body1">
            <strong>
              <Trans>2. Legal bases of the processing</Trans>
            </strong>
          </Typography>
          <Typography variant="body1">
            <Trans>
              The processing of personal data requires a legal basis, which we would like to present
              to you below. For processing of personal data for which we obtain the consent of the
              data subject, Article 6(1)(a) of the EU General Data Protection Regulation (GDPR)
              serves as the legal basis. When processing personal data that is necessary for the
              performance of a contract to which the data subject is a party, Article 6(1)(b) DSGVO
              serves as the legal basis. This also includes processing operations that are necessary
              for the performance of pre-contractual measures. If processing of personal data is
              necessary for compliance with a legal obligation to which our company is subject,
              Article 6 (1) c DSGVO serves as the legal basis. If processing is necessary to protect
              a legitimate interest of our company or a third party and if the interests,
              fundamental rights and freedoms of the data subject do not override the former
              interest, Article 6(1)(f) DSGVO serves as the legal basis for the processing. The
              legitimate interest of our company lies in the performance of our business activities
              as well as in the analysis, optimization and maintenance of the security of our online
              offer.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <strong>
              <Trans>II. Log files, hosting</Trans>
            </strong>
          </Typography>
          <Typography variant="body1">
            <Trans>
              In the server statistics, data is automatically stored that the browser transmits to
              us as part of our legitimate interest in analysis and for security reasons (so-called
              "log files").
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>In detail, these are the following data:</Trans>
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            <Typography component="li" variant="body1">
              <Trans>Language and version of the browser software</Trans>
            </Typography>
            <Typography component="li" variant="body1">
              <Trans>Operating system used and its interface</Trans>
            </Typography>
            <Typography component="li" variant="body1">
              <Trans>Referrer URL (the previously visited page)</Trans>
            </Typography>
            <Typography component="li" variant="body1">
              <Trans>Host name of the accessing computer (IP address)</Trans>
            </Typography>
            <Typography component="li" variant="body1">
              <Trans>Date and time of the server request</Trans>
            </Typography>
            <Typography component="li" variant="body1">
              <Trans>Time zone difference from Greenwich Mean Time (GMT)</Trans>
            </Typography>
            <Typography component="li" variant="body1">
              <Trans>Content of the request (concrete page)</Trans>
            </Typography>
            <Typography component="li" variant="body1">
              <Trans>Data volume transferred</Trans>
            </Typography>
            <Typography component="li" variant="body1">
              <Trans>Access status/ HTTP status code.</Trans>
            </Typography>
          </Box>
          <Typography variant="body1">
            <Trans>
              As a rule, we cannot assign this data to specific persons. A combination of this data
              with other data sources is not made. The data is also deleted within 7 days after a
              statistical evaluation. Data whose further retention is required for evidentiary
              purposes is exempt from deletion until the respective incident has been finally
              clarified.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              We make use of hosting services. These serve to provide infrastructure and platform
              services, computing capacity, storage space and database services, security services
              and technical maintenance services to maintain the operation of this online offer.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              In doing so, we or our hosting provider process inventory data, contact data, content
              data, contract data, usage data, meta data and communication data of customers,
              interested parties and visitors of this online offer on the basis of our legitimate
              interests in an efficient and secure provision of this online offer.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <strong>
              <Trans>III. Contractual processing</Trans>
            </strong>
          </Typography>
          <Typography variant="body1">
            <Trans>
              The personal data provided by you for contractual purposes when booking a stay, such
              as name, address, e-mail address or number of guests, will be used to process your
              booking, answer your inquiries or provide you with access to certain contractual
              information. As described below, this includes sharing relevant data with our payment
              provider and the campground operator where necessary to fulfill your reservation.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              To process payment for your booking, we use the payment service provider Stripe
              (Stripe Payments Europe, Limited, 1 Grand Canal Street Lower, Grand Canal Dock,
              Dublin, Ireland, together with its affiliate Stripe, Inc., 354 Oyster Point Blvd,
              South San Francisco, CA 94080, USA, "Stripe"). Stripe processes the payment data you
              provide during checkout (such as card details and billing address) on our behalf in
              order to carry out the payment. The legal basis for this processing is Article 6(1)(b)
              DSGVO, as it is necessary for the performance of the booking contract. For more
              information, please see Stripe's privacy policy at{" "}
              <MuiLink href="https://stripe.com/privacy" target="_blank" rel="noreferrer">
                https://stripe.com/privacy
              </MuiLink>
              .
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              Your booking request, including the personal data you provide, is also transmitted to
              and processed by the operator of the campground you are booking with, so that your
              reservation can be accepted and fulfilled. Depending on our arrangement with the
              respective campground, CampNerd and the campground operator act either as joint
              controllers or as controller and processor for this purpose. If you have questions
              about how a specific campground handles your data, please contact us using the details
              above and we will put you in touch with the responsible party.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <strong>
              <Trans>IV. Contact</Trans>
            </strong>
          </Typography>
          <Typography variant="body1">
            <Trans>
              When you contact us, e.g. by e-mail, your details will be stored by us in order to
              answer your questions. A transfer to third parties does not take place in principle,
              unless applicable data protection regulations justify a transfer or we are legally
              obliged to do so. You can revoke your consent at any time with effect for the future.
              In the event of revocation, your data will be deleted immediately, unless there is a
              legal exception for further processing. Your data will otherwise be deleted when we
              have processed your request or the purpose of storage has ceased to apply and there
              are no other legal exceptions to the contrary.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <strong>
              <Trans>V. Cookies and browser storage</Trans>
            </strong>
          </Typography>
          <Typography variant="body1">
            <Trans>
              This site uses cookies. Cookies are small text files that are stored on your computer
              and through which the entity that sets the cookie receives certain information. They
              are used to make the Internet offer more user-friendly and effective or/and to
              facilitate your navigation on our website. We set cookies that are not absolutely
              necessary only with your consent. You can revoke this consent at any time for future
              use.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              Consent is voluntary and you can also use our website without accepting cookies. You
              can also configure your browser settings according to your wishes and, for example,
              refuse to accept third party cookies or all cookies or delete cookies that have
              already been stored. If you do not accept cookies, please note that in this case our
              offer may not work properly. If we do not provide any other information on the
              individual topics mentioned in this data protection declaration or in the cookie
              banner, the lifetime of the cookies refers to 24 months. You can find out which
              function on our website sets cookies below under the individual functions.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              In addition to cookies, we use your browser's session storage to temporarily save your
              booking progress, including the personal details you have entered, so that this
              information is not lost if you navigate between steps or reload the page. This
              information is automatically deleted once your booking is confirmed or when you close
              your browser tab.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              We use your browser's local storage to remember your cookie consent choice, currency
              selection and other display preferences. Unlike cookies, these entries do not expire
              automatically; they remain until you clear your browser data or change the
              corresponding setting.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <strong>
              <Trans>
                VI. Disclosure of data to third parties: General and contractual purpose
              </Trans>
            </strong>
          </Typography>
          <Typography variant="body1">
            <Trans>
              We pass on data to third parties if this is necessary for the fulfillment of the
              contract and / or we are legally obligated and / or authorized to do so in individual
              cases.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <strong>
              <Trans>
                VII. Disclosure of data to third parties: tools for the economic operation of the
                website
              </Trans>
            </strong>
          </Typography>
          <Typography variant="body1">
            <Trans>
              In part, we use external service providers within the scope of your consent or our
              legitimate interests with regard to analysis, optimization and economic operation of
              the online offer. If you have given your consent for tools that are not necessary for
              the operation of the website, you can also have these settings changed again at any
              time. We list our service providers below.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              If your data is to be used for other purposes, we will inform you in advance and only
              use the data if you have previously given your express further consent.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>1. PostHog</Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              Within the scope of your consent, we use services for the optimization and economic
              operation of our online offer for which PostHog Inc., 2261 Market Street #4008, San
              Francisco, CA 94114, USA ("PostHog") is responsible. Data is processed on servers
              located in the European Union.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>a. PostHog Analytics</Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              This website uses PostHog as a web analytics service based on your consent. PostHog
              uses cookies and comparable technologies to help us analyze how users use the booking
              widget, including which steps of the booking flow are used and completed, your
              selected dates and number of guests, the accommodation and extras you choose, prices
              and currency, and the campground and affiliate you booked through. With your consent,
              PostHog may also record your on-screen interactions (session recording) to help us
              understand and improve the booking experience; input fields and personal details shown
              to you — such as your name, address, contact details, travel companions, vehicle
              registration and any message you write — are always masked and never captured in these
              recordings.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              You can revoke your consent at any time with future effect by accessing the cookie
              settings of our website and changing your selection there. The legality of the
              processing carried out on the basis of the consent until the revocation remains
              unaffected.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              For the purpose and scope of the data collection and the further processing and use of
              the data by PostHog, as well as your rights in this regard and setting options for
              protecting your privacy, please refer to PostHog's privacy policy at{" "}
              <MuiLink href="https://posthog.com/privacy" target="_blank" rel="noreferrer">
                https://posthog.com/privacy
              </MuiLink>
              .
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>2. Sentry</Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              We use Sentry (Functional Software, Inc., 45 Fremont Street, 8th Floor, San Francisco,
              CA 94105, USA) to detect and diagnose technical errors and to measure the performance
              of our website, on the basis of our legitimate interest in operating a secure and
              reliable booking service (Article 6(1)(f) DSGVO). Sentry is used independently of your
              cookie consent choice, as it serves the technical security of our online offer rather
              than analysis of your usage behavior. Error reports can contain technical information
              about your browser and device, details about the error, and, where relevant to
              diagnosing a specific issue, your booking or payment reference. For more information,
              please refer to Sentry's privacy policy at{" "}
              <MuiLink href="https://sentry.io/privacy/" target="_blank" rel="noreferrer">
                https://sentry.io/privacy/
              </MuiLink>
              .
            </Trans>
          </Typography>
          <Typography variant="body1">
            <strong>
              <Trans>VIII. Data storage outside the EU/EEA</Trans>
            </strong>
          </Typography>
          <Typography variant="body1">
            <Trans>
              As described in Section III above, we use Stripe as our payment service provider,
              which has an affiliate in the United States. Insofar as this is necessary to process
              your payment, it may happen that your payment and billing data is processed outside
              the European Economic Area, where a level of data protection corresponding to the
              European standard is not consistently ensured and confirmed. Therefore, it cannot be
              ruled out in particular that security authorities in a third country may gain access
              to your data without you being able to take legally effective action against them.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              This transfer takes place on the basis of Article 6(1)(b) DSGVO, as it is necessary
              for the performance of the booking contract, and is additionally safeguarded by EU
              standard contractual clauses agreed with Stripe pursuant to Art. 46 DSGVO.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              In the opinion of some US third-party providers, a level of protection corresponding
              to the European standard is already guaranteed due to the conclusion of so-called
              standard contractual clauses as well as additional measures taken in the sense of the
              Schrems II case law. However, since the suitability of such measures to ensure a
              sufficient level of data protection is disputed, we limit the transfer of personal
              data to what is strictly necessary to process your payment.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <strong>
              <Trans>IX. Rights of the data subject</Trans>
            </strong>
          </Typography>
          <Typography variant="body1">
            <Trans>
              You have a right to information about the personal data stored by us about you.
              According to the legal provisions, you also have the right to correct incorrect data,
              blocking, data portability and deletion of your personal data. To do this, send us an
              e-mail with the subject "Data protection".
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              You also have the right to lodge a complaint with a data protection supervisory
              authority if you believe that processing of personal data relating to you is in breach
              of the law.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              If you have given your consent, you have the right to revoke your consent at any time.
              This does not affect the lawfulness of the processing carried out on the basis of the
              consent until the revocation.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              You may object to the future processing of your personal data in accordance with the
              legal requirements at any time. The objection can be made in particular against the
              processing for purposes of direct advertising.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <strong>
              <Trans>X. Storage period of the personal data</Trans>
            </strong>
          </Typography>
          <Typography variant="body1">
            <Trans>
              If we have not provided storage information on the specific points, the following
              applies: We store personal data for the duration of the respective statutory retention
              period or as long as the purpose of the collection exists. After expiry of the
              retention period, the data is routinely deleted, unless it is necessary for the
              initiation or performance of a contract. If user data is not deleted because it is
              required for other and legally permissible purposes, its processing will be restricted
              as far as possible. Accordingly, the data will be blocked if possible and not
              processed for other purposes. This applies, for example, to user data that must be
              retained for reasons of commercial or tax law.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <strong>
              <Trans>XI. Safety information</Trans>
            </strong>
          </Typography>
          <Typography variant="body1">
            <Trans>
              We make every effort to process your personal data by taking all technical and
              organizational possibilities to comply with the provisions of data protection laws and
              thus to protect this data. Our website or the communication via our website with us is
              encrypted via HTTPS.
            </Trans>
          </Typography>
          <Typography variant="body1">
            <Trans>
              This privacy policy was provided by the Sieling Law Firm – Specialist law firm for IT
              law.
            </Trans>
          </Typography>
        </Stack>
      </Container>
    </AppLayout>
  );
}
